import React, { useEffect, useMemo, useState } from "react";
import { addLayer } from "../../../utils/addingLayers/AddVoltagesLayers";
import { GeoJsonLayer } from "@deck.gl/layers";

const MVHierarchy = ({
    isDalekovodActive,
    wfsLayers,
    setWfsLayers,
    setHoverInfo,
    handleClickInfo,
    setWmsLayers,
    viewState,
    setViewState,
    setOverlay,
    id_terenske_jedinice,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [expandedVods, setExpandedVods] = useState([]);
    const [expandedDionice, setExpandedDionice] = useState([]);
    const [expandedStubovi, setExpandedStubovi] = useState([]);

    const getFeatures = (layer) => {
        if (!layer?.props) return [];

        if (layer.props.data?.features) {
            return layer.props.data.features;
        }

        if (Array.isArray(layer.props.data)) {
            return layer.props.data;
        }

        return [];
    };

    const stuboviLayer = useMemo(
        () =>
            wfsLayers.find(
                (layer) => layer.id === "stubovi-srednjenaponska-layer"
            ),
        [wfsLayers]
    );

    const vodLayer = useMemo(
        () =>
            wfsLayers.find(
                (layer) => layer.id === "vod-srednjenaponska-layer"
            ),
        [wfsLayers]
    );

    const dionicaLayer = useMemo(
        () =>
            wfsLayers.find(
                (layer) => layer.id === "dionica-srednjenaponska-layer"
            ),
        [wfsLayers]
    );

    const stubovi = useMemo(() => getFeatures(stuboviLayer), [stuboviLayer]);
    const vodovi = useMemo(() => getFeatures(vodLayer), [vodLayer]);
    const dionice = useMemo(() => getFeatures(dionicaLayer), [dionicaLayer]);

    const hierarchy = useMemo(() => {
        const stuboviPoDionici = {};

        stubovi.forEach((stub) => {
            const dionicaId = stub.properties?.pripadnost_dionici;
            const stubId = stub.properties?.id_stubnog_mjesta;

            if (!dionicaId || !stubId) return;

            const key = String(dionicaId);

            if (!stuboviPoDionici[key]) {
                stuboviPoDionici[key] = [];
            }

            stuboviPoDionici[key].push(stubId);
        });

        const result = {};

        dionice.forEach((dionica) => {
            const nazivVoda = dionica.properties?.pripadnost_vodu;
            const idDionice = dionica.properties?.id_dionica;

            if (!nazivVoda || !idDionice) return;

            if (!result[nazivVoda]) {
                result[nazivVoda] = {};
            }

            result[nazivVoda][idDionice] =
                stuboviPoDionici[String(idDionice)] || [];
        });

        return Object.keys(result)
            .sort((a, b) =>
                String(a).localeCompare(String(b), "sr", { numeric: true })
            )
            .reduce((acc, vod) => {
                acc[vod] = result[vod];
                return acc;
            }, {});
    }, [stubovi, dionice]);

    const fetchTrafoAndVodData = async () => {
        if (isLoading) return;

        setIsLoading(true);

        const getAll = false;

        try {
            const requests = [];

            if (!stuboviLayer) {
                requests.push(
                    addLayer(
                        "stubovi",
                        setHoverInfo,
                        handleClickInfo,
                        null,
                        setWmsLayers,
                        "srednjenaponska",
                        viewState,
                        setViewState,
                        getAll,
                        setOverlay,
                        id_terenske_jedinice,
                        wfsLayers
                    )
                );
            } else {
                requests.push(Promise.resolve([]));
            }

            if (!vodLayer) {
                requests.push(
                    addLayer(
                        "vod",
                        setHoverInfo,
                        handleClickInfo,
                        null,
                        setWmsLayers,
                        "srednjenaponska",
                        viewState,
                        setViewState,
                        getAll,
                        setOverlay,
                        id_terenske_jedinice,
                        wfsLayers
                    )
                );
            } else {
                requests.push(Promise.resolve([]));
            }

            if (!dionicaLayer) {
                requests.push(
                    addLayer(
                        "dionica",
                        setHoverInfo,
                        handleClickInfo,
                        null,
                        setWmsLayers,
                        "srednjenaponska",
                        viewState,
                        setViewState,
                        getAll,
                        setOverlay,
                        id_terenske_jedinice,
                        wfsLayers
                    )
                );
            } else {
                requests.push(Promise.resolve([]));
            }

            const responses = await Promise.all(requests);
            const newLayers = responses.flat().filter(Boolean);

            if (newLayers.length > 0) {
                setWfsLayers((prevLayers) => {
                    const existingIds = new Set(
                        prevLayers.map((layer) => layer.id)
                    );

                    const filteredNewLayers = newLayers.filter(
                        (layer) => !existingIds.has(layer.id)
                    );

                    return [...prevLayers, ...filteredNewLayers];
                });
            }
        } catch (error) {
            console.error("Greska prilikom ucitavanja slojeva:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isDalekovodActive) return;

        if (!stuboviLayer || !vodLayer || !dionicaLayer) {
            fetchTrafoAndVodData();
        }
    }, [isDalekovodActive, stuboviLayer, vodLayer, dionicaLayer]);

    const getDynamicWidth = (zoom) => Math.max(2, zoom * 0.8);
    const getDynamicRadius = (zoom) => Math.max(4, zoom * 0.8);

    const getLayerColor = (type) => {
        if (type === "vod") return [255, 255, 0, 230];
        if (type === "dionica") return [255, 0, 0, 255];
        if (type === "stub") return [0, 80, 255, 255];

        return [0, 0, 0, 255];
    };

    const getLayerWidth = (type) => {
        if (type === "vod") return Math.max(5, viewState.zoom * 1.1);
        if (type === "dionica") return Math.max(4, viewState.zoom * 0.9);
        if (type === "stub") return Math.max(4, viewState.zoom * 0.8);

        return getDynamicWidth(viewState.zoom);
    };

    const makeSafeId = (value) => {
        return String(value)
            .trim()
            .replace(/\s+/g, "-")
            .replace(/[^\w-]/g, "");
    };

    const toggleHighlightLayer = (id, featureOrCollection, type) => {
        const existingLayer = wfsLayers.find((layer) => layer.id === id);

        if (existingLayer) {
            setWfsLayers((prevLayers) =>
                prevLayers.filter((layer) => layer.id !== id)
            );
            return;
        }

        const color = getLayerColor(type);

        const data =
            featureOrCollection?.type === "FeatureCollection"
                ? featureOrCollection
                : {
                    type: "FeatureCollection",
                    features: [featureOrCollection],
                };

        const newLayer = new GeoJsonLayer({
            id,
            data,
            getLineColor: color,
            getFillColor: color,
            getPointRadius: () => getDynamicRadius(viewState.zoom),
            getLineWidth: () => getLayerWidth(type),
            updateTriggers: {
                getLineWidth: [viewState.zoom, type],
                getPointRadius: [viewState.zoom],
            },
            lineWidthUnits: "pixels",
            radiusUnits: "pixels",
            pointRadiusMinPixels: 4,
            lineWidthMinPixels: 2,
            pickable: true,
            onHover: (info) => setHoverInfo(info),
            onClick: (info) => handleClickInfo(info),
        });

        setWfsLayers((prevLayers) => [...prevLayers, newLayer]);
    };

    const toggleVod = (vod) => {
        const dioniceVoda = dionice.filter(
            (dionica) =>
                String(dionica.properties?.pripadnost_vodu) === String(vod)
        );

        if (dioniceVoda.length > 0) {
            toggleHighlightLayer(
                `highlight-vod-${makeSafeId(vod)}`,
                {
                    type: "FeatureCollection",
                    features: dioniceVoda,
                },
                "vod"
            );
        } else {
            const vodFeature = vodovi.find((feature) => {
                const p = feature.properties || {};

                return (
                    String(p.naziv_voda) === String(vod) ||
                    String(p.pripadnost_vodu) === String(vod) ||
                    String(p.id_vod) === String(vod) ||
                    String(p.sifra_voda) === String(vod) ||
                    String(p.naziv) === String(vod)
                );
            });

            if (vodFeature) {
                toggleHighlightLayer(
                    `highlight-vod-${makeSafeId(
                        vodFeature.properties?.id_vod || vod
                    )}`,
                    vodFeature,
                    "vod"
                );
            } else {
                console.warn("Nije pronadjen vod:", vod);
            }
        }

        setExpandedVods((prev) =>
            prev.includes(vod)
                ? prev.filter((item) => item !== vod)
                : [...prev, vod]
        );
    };

    const toggleDionica = (dionicaId) => {
        const feature = dionice.find(
            (dionica) =>
                String(dionica.properties?.id_dionica) === String(dionicaId)
        );

        if (feature) {
            toggleHighlightLayer(
                `highlight-dionica-${makeSafeId(dionicaId)}`,
                feature,
                "dionica"
            );
        }

        setExpandedDionice((prev) =>
            prev.includes(dionicaId)
                ? prev.filter((item) => item !== dionicaId)
                : [...prev, dionicaId]
        );
    };

    const toggleStub = (stubId) => {
        const feature = stubovi.find(
            (stub) =>
                String(stub.properties?.id_stubnog_mjesta) === String(stubId)
        );

        if (feature) {
            toggleHighlightLayer(
                `highlight-stub-${makeSafeId(stubId)}`,
                feature,
                "stub"
            );
        }

        setExpandedStubovi((prev) =>
            prev.includes(stubId)
                ? prev.filter((item) => item !== stubId)
                : [...prev, stubId]
        );
    };

    if (isLoading && Object.keys(hierarchy).length === 0) {
        return <div className="loading-spinner"></div>;
    }

    return (
        <div>
            <ul className="transformerAreaList">
                {Object.keys(hierarchy).map((vod) => (
                    <li key={vod}>
                        <span
                            onClick={() => toggleVod(vod)}
                            style={{
                                cursor: "pointer",
                                fontWeight: "bold",
                                color: expandedVods.includes(vod)
                                    ? "#ffffff"
                                    : "#9e9d9d",
                            }}
                        >
                            Vod: {vod}
                        </span>

                        {expandedVods.includes(vod) && (
                            <ul className="sub-options">
                                {Object.keys(hierarchy[vod])
                                    .sort((a, b) =>
                                        String(a).localeCompare(String(b), "sr", {
                                            numeric: true,
                                        })
                                    )
                                    .map((dionica) => (
                                        <li key={dionica}>
                                            <span
                                                onClick={() =>
                                                    toggleDionica(dionica)
                                                }
                                                style={{
                                                    cursor: "pointer",
                                                    fontWeight: "bold",
                                                    color: expandedDionice.includes(
                                                        dionica
                                                    )
                                                        ? "#ffffff"
                                                        : "#9e9d9d",
                                                }}
                                            >
                                                Dionica: {dionica}
                                            </span>

                                            {expandedDionice.includes(dionica) && (
                                                <ul>
                                                    {hierarchy[vod][dionica]
                                                        .sort((a, b) =>
                                                            String(
                                                                a
                                                            ).localeCompare(
                                                                String(b),
                                                                "sr",
                                                                {
                                                                    numeric: true,
                                                                }
                                                            )
                                                        )
                                                        .map((stub) => (
                                                            <li key={stub}>
                                                                <span
                                                                    onClick={() =>
                                                                        toggleStub(
                                                                            stub
                                                                        )
                                                                    }
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontWeight:
                                                                            "bold",
                                                                        color: expandedStubovi.includes(
                                                                            stub
                                                                        )
                                                                            ? "#ffffff"
                                                                            : "#9e9d9d",
                                                                    }}
                                                                >
                                                                    Stub: {stub}
                                                                </span>
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default MVHierarchy;